import { useMemo, useSyncExternalStore } from "react";
import {
  ANCHOR_BOARD_DISPLAY_NAME,
  BOARD_CONFIG,
  IS_MIX_CATALOG,
  MIX_BOARD_NAMES,
  MIX_REGISTERED_SLUGS,
} from "@/config/browserBoard";
import { formatBoardDisplayName } from "@/config/formatBoardDisplayName";
import { isValidBoardSlug, MIX_PUBLIC_BASE_PATH } from "@/config/mixLineup";
import {
  getAddedBoardNames,
  subscribeAddedBoardNames,
} from "@/shared/lib/addedBoardNames";
import { parseMixRouteThreadSlug } from "@/shared/lib/threadRoutePath";

/**
 * スレッドページ一式が参照する「対象板スコープ」。
 *
 * /mix配下の /{slug}/thread/{id} ではURLのslugが真実源になる
 * (ThreadStateは板フィールドを持たない)。板mountとslug無しルートでは
 * 現在板(BOARD_CONFIG)へ縮退し、従来挙動と一致する。
 */
export type ThreadBoardScope = Readonly<{
  /** 対象板slug。X-Board-Key(添付presign・スレ内検索・投稿options)に使う。 */
  boardKey: string;
  /**
   * タイトル/OGP/ヘッダ用の表示名。スレページは個別板を指す面なので、
   * mixでも総合名ではなく対象板の `{板名}@あいもげ` になる。
   */
  displayName: string;
  /** canonical URLの基底。mix内スレでは板ネイティブの /b/{slug}。 */
  canonicalBasePath: string;
}>;

export type ThreadBoardScopeEnvironment = Readonly<{
  isMixCatalog: boolean;
  anchorBoardKey: string;
  anchorDisplayName: string;
  anchorPublicBasePath: string;
  registeredSlugs: readonly string[];
  boardNames: Readonly<Record<string, string>>;
}>;

const MODULE_ENVIRONMENT: ThreadBoardScopeEnvironment = {
  isMixCatalog: IS_MIX_CATALOG,
  anchorBoardKey: BOARD_CONFIG.boardKey,
  // スレページ専用の縮退先なので、mixでも総合名ではなくアンカー板名を出す
  // (/mix/thread/{id} のようなslug無し形はアンカー板スコープになる)
  anchorDisplayName: ANCHOR_BOARD_DISPLAY_NAME,
  anchorPublicBasePath: BOARD_CONFIG.publicBasePath,
  registeredSlugs: MIX_REGISTERED_SLUGS,
  boardNames: MIX_BOARD_NAMES,
};

/** route paramのslugから対象板スコープを導出する。環境はテスト用に注入可能。 */
export function resolveThreadBoardScope(
  slugParam: string | undefined,
  environment: ThreadBoardScopeEnvironment = MODULE_ENVIRONMENT,
): ThreadBoardScope {
  if (
    environment.isMixCatalog &&
    slugParam !== undefined &&
    isValidBoardSlug(slugParam)
  ) {
    return {
      boardKey: slugParam,
      displayName: formatBoardDisplayName({
        boardName:
          environment.boardNames[slugParam] ??
          getAddedBoardNames()[slugParam] ??
          slugParam,
      }),
      canonicalBasePath: `/b/${slugParam}`,
    };
  }
  return {
    boardKey: environment.anchorBoardKey,
    displayName: environment.anchorDisplayName,
    canonicalBasePath: environment.anchorPublicBasePath,
  };
}

/**
 * mount部を含む生pathnameから対象板slugを取り出す。板slug付き形
 * (/mix/{slug}/thread/{id})でなければundefined。
 *
 * 解析本体はrouter pathname用の共有パーサに委譲し、ここはmountプレフィックスを
 * 外すアダプタに留める(slug抽出の実装を1本に保つ)。
 */
export function parseMixThreadSlug(pathname: string): string | undefined {
  if (!pathname.startsWith(`${MIX_PUBLIC_BASE_PATH}/`)) return undefined;
  // mountを外すとrouterのpathname("/{slug}/thread/{id}")と同じ形になる
  return parseMixRouteThreadSlug(pathname.slice(MIX_PUBLIC_BASE_PATH.length));
}

function subscribeToHistory(onChange: () => void): () => void {
  window.addEventListener("popstate", onChange);
  return (): void => window.removeEventListener("popstate", onChange);
}

/**
 * 現在URLの対象板スコープを読む。useFromMixCatalogと同じく生のlocationを
 * 読む(routerコンテキスト外のテスト描画でも安全)。router遷移はスレページの
 * 再マウント時にsnapshotを再検査して追従する。
 *
 * @tanstack/historyは `pushState` をmicrotaskへ遅延させるため、SPA遷移を
 * 起こしたrenderでは `window.location.pathname` がまだ前のURLである。
 * 遷移で再マウントしない常設UI(ヘッダ等)がrender時に対象板を出す場合は、
 * このhookではなくrouterのpathname経由(`parseMixRouteThreadSlug` +
 * `resolveThreadBoardScope`)で解決する。
 */
export function useThreadBoardScope(): ThreadBoardScope {
  const pathname = useSyncExternalStore(
    subscribeToHistory,
    // preact/compatはgetServerSnapshotを受け取らず捨てるため、window不在の
    // 保護はgetSnapshot側で行う(useFromMixCatalogと同じ形)。
    () => (typeof window === "undefined" ? "" : window.location.pathname),
    () => "",
  );
  const slug = parseMixThreadSlug(pathname);
  const addedNames = useSyncExternalStore(
    subscribeAddedBoardNames,
    getAddedBoardNames,
    getAddedBoardNames,
  );
  return useMemo(
    () =>
      resolveThreadBoardScope(slug, {
        ...MODULE_ENVIRONMENT,
        boardNames: { ...MODULE_ENVIRONMENT.boardNames, ...addedNames },
      }),
    [slug, addedNames],
  );
}
